/**
 * @typedef {import('./ImageData').default} ImageData
 * @typedef {import('../PropertyData').default} PropertyData
 * @typedef {import('../../shared/PropertyService/EditablePropertyDetails').default} EditablePropertyDetails
 */
import apiUrls from '../../../config/local/api-urls';
import HttpClient from '@mooveguru/js-http-client';
// @ts-ignore
const httpClient = new HttpClient();

const PROPERTY_API_FIELDS = Object.freeze({
	amortizationYears: 'amortization_years',
	amountToLoan: 'amount_to_loan',
	buildYear: 'build_year',
	constructionType: 'construction_type',
	dueDate: 'due_date',
	escrowInsuranceAmount: 'escrow_insurance_amount',
	escrowInsuranceFrequency: 'escrow_insurance_frequency',
	escrowInsuranceMonthly: 'escrow_insurance_monthly',
	escrowTaxAmount: 'escrow_tax_amount',
	escrowTaxFrequency: 'escrow_tax_frequency',
	escrowTaxMonthly: 'escrow_tax_monthly',
	floorCover: 'floor_cover',
	foundationType: 'foundation_type',
	garageCars: 'garage_cars',
	garageType: 'garage_type',
	heatingFuelType: 'heating_fuel_type',
	interiorWalls: 'interior_walls',
	isPrimary: 'is_primary',
	lenderName: 'lender_name',
	loanType: 'loan_type',
	lotSizeSquareFootage: 'lot_size_square_footage',
	maximumInterestRate: 'maximum_interest_rate',
	numberOfBaths: 'number_of_baths',
	numberOfPartialBaths: 'number_of_partial_baths',
	originalDateOfContract: 'original_date_of_contract',
	otherRooms: 'other_rooms',
	plumbingFixtures: 'plumbing_fixtures',
	postalCode: 'postal_code',
	roofCover: 'roof_cover',
	roofType: 'roof_type',
	streetAddress1: 'street_address_1',
	streetAddress2: 'street_address_2',
	totalNumberOfRooms: 'total_number_of_rooms',
});

/**
 * @param {PropertyData} property
 * @param {string} accessToken
 * @returns {Promise<void>}
 * @throws {Error}
 */
export async function addProperty(property, accessToken) {
	const homeownerResponse = await httpClient.get(
		apiUrls.me.homeowner.root,
		new Headers({ 'X-Access-Token': accessToken })
	);

	if (!homeownerResponse.isOk) {
		throw new Error(homeownerResponse.body.message);
	}

	const response = await httpClient.post(
		`${apiUrls.property.root}/${homeownerResponse.body.results.id}`,
		new Headers({
			Authorization: `Bearer ${accessToken}`,
			'Content-Type': 'application/json',
		}),
		{
			city: property.address.city,
			country: 'US',
			[PROPERTY_API_FIELDS.isPrimary]: property.isPrimary,
			[PROPERTY_API_FIELDS.postalCode]: property.address.postalCode,
			state: property.address.state,
			[PROPERTY_API_FIELDS.streetAddress1]:
				property.address.streetAddress1,
			[PROPERTY_API_FIELDS.streetAddress2]:
				property.address.streetAddress2,
			title: property.title,
		}
	);

	if (!response.isOk) {
		throw new Error(response.body.message);
	}

	return response.body.results.property_id;
}

/**
 * @param {string} propertyId
 * @param {string} accessToken
 * @returns {Promise<object>}
 * @throws {Error}
 */
export async function getProperty(propertyId, accessToken) {
	const response = await httpClient.get(
		`${apiUrls.property.root}/${propertyId}`,
		new Headers({ Authorization: `Bearer ${accessToken}` })
	);

	if (!response.isOk) {
		throw new Error(response.body.message);
	}

	const results = response.body.results;

	return {
		address: {
			city: results.address.city,
			country: results.address.country,
			postalCode: results.address.postal_code,
			state: results.address.state,
			streetAddress1: results.address.street_address_1,
			streetAddress2: results.address.street_address_2,
		},
		category: results.category ?? null,
		details: {
			acreage: results.propertyDetails.lotsizeAcres,
			amountToLoan: results.propertyDetails.loanAmount,
			bedrooms: results.propertyDetails.numberOfBedrooms,
			buildYear: results.propertyDetails.yearBuilt,
			constructionType: results.propertyDetails.typeConstruction,
			dueDate: results.propertyDetails.dueDate,
			fireplace: results.propertyDetails.fireplace,
			foundationType: results.propertyDetails.foundation,
			garage: results.propertyDetails.garageType,
			heating: results.propertyDetails.heating,
			heatingFuelType: results.propertyDetails.heatingFuelType,
			lenderName: results.propertyDetails.lenderName,
			loanType: results.propertyDetails.loanType,
			lotSizeSquareFootage: results.propertyDetails.lotsizeSquareFeet,
			maximumInterestRate: results.propertyDetails.maximumInterestRate,
			numberOfBaths: results.propertyDetails.numberOfBaths,
			numberOfPartialBaths: results.propertyDetails.numberOfPartialBaths,
			originalDateOfContract:
				results.propertyDetails.originalDateOfContract,
			otherRooms: results.propertyDetails.otherRooms,
			pool: results.propertyDetails.pool,
			rate: results.propertyDetails.interestRate,
			roof: results.propertyDetails.roofType,
			sewer: results.propertyDetails.sewer,
			stories: results.propertyDetails.noOfStories,
			totalNumberOfRooms: results.propertyDetails.totalNumberOfRooms,
			water: results.propertyDetails.water,
		},
		id: results.id,
		image:
			results.images?.find(
				/**
				 * @param {ImageData} image
				 * @returns {boolean}
				 */
				(image) => image.type === 'street_view'
			) ?? '',
		isPrimary: results.is_primary,
		title: results.title,
	};
}

/**
 * @param {string} id
 * @param {EditablePropertyDetails} data
 * @param {string} accessToken
 * @returns {Promise<void>}
 * @throws {Error}
 */
export async function updateProperty(id, data, accessToken) {
	const mappedData = {
		acreage: data?.details?.acreage,
		[PROPERTY_API_FIELDS.amountToLoan]: data?.details?.amountToLoan,
		[PROPERTY_API_FIELDS.amortizationYears]:
			data?.details?.amortizationYears,
		basement: data?.details?.basement,
		bedrooms: data?.details?.bedrooms,
		[PROPERTY_API_FIELDS.buildYear]: data?.details?.buildYear,
		category: data?.category ?? null,
		city: data?.address?.city,
		[PROPERTY_API_FIELDS.constructionType]: data?.details?.constructionType,
		country: data?.address?.country,
		[PROPERTY_API_FIELDS.dueDate]: data?.details?.dueDate,
		[PROPERTY_API_FIELDS.escrowInsuranceAmount]:
			data?.details?.escrowInsuranceAmount,
		[PROPERTY_API_FIELDS.escrowInsuranceFrequency]:
			data?.details?.escrowInsuranceFrequency,
		[PROPERTY_API_FIELDS.escrowInsuranceMonthly]:
			data?.details?.escrowInsuranceMonthly,
		[PROPERTY_API_FIELDS.escrowTaxAmount]: data?.details?.escrowTaxAmount,
		[PROPERTY_API_FIELDS.escrowTaxFrequency]:
			data?.details?.escrowTaxFrequency,
		[PROPERTY_API_FIELDS.escrowTaxMonthly]: data?.details?.escrowTaxMonthly,
		fireplace: data?.details?.fireplace,
		[PROPERTY_API_FIELDS.floorCover]: data?.details?.floorCover,
		[PROPERTY_API_FIELDS.foundationType]: data?.details?.foundationType,
		[PROPERTY_API_FIELDS.garageCars]: data?.details?.garageCars,
		[PROPERTY_API_FIELDS.garageType]: data?.details?.garageType,
		heating: data?.details?.heating,
		[PROPERTY_API_FIELDS.heatingFuelType]: data?.details?.heatingFuelType,
		[PROPERTY_API_FIELDS.interiorWalls]: data?.details?.interiorWalls,
		[PROPERTY_API_FIELDS.lenderName]: data?.details?.lenderName,
		[PROPERTY_API_FIELDS.loanType]: data?.details?.loanType,
		[PROPERTY_API_FIELDS.lotSizeSquareFootage]:
			data?.details?.lotSizeSquareFootage,
		[PROPERTY_API_FIELDS.maximumInterestRate]:
			data?.details?.maximumInterestRate,
		[PROPERTY_API_FIELDS.numberOfBaths]: data?.details?.numberOfBaths,
		[PROPERTY_API_FIELDS.numberOfPartialBaths]:
			data?.details?.numberOfPartialBaths,
		[PROPERTY_API_FIELDS.originalDateOfContract]:
			data?.details?.originalDateOfContract,
		[PROPERTY_API_FIELDS.otherRooms]: data?.details?.otherRooms,
		[PROPERTY_API_FIELDS.plumbingFixtures]: data?.details?.plumbingFixtures,
		pool: data?.details?.pool,
		[PROPERTY_API_FIELDS.postalCode]: data?.address?.postalCode?.toString(),
		rate: data?.details?.rate,
		[PROPERTY_API_FIELDS.roofCover]: data?.details?.roofCover,
		[PROPERTY_API_FIELDS.roofType]: data?.details?.roofType,
		sewer: data?.details?.sewer,
		state: data?.address?.state,
		stories: data?.details?.stories,
		[PROPERTY_API_FIELDS.streetAddress1]: data?.address?.streetAddress1,
		[PROPERTY_API_FIELDS.streetAddress2]: data?.address?.streetAddress2,
		style: data?.details?.style,
		title: data?.title,
		[PROPERTY_API_FIELDS.totalNumberOfRooms]:
			data?.details?.totalNumberOfRooms,
		water: data?.details?.water,
	};

	const response = await httpClient.put(
		`${apiUrls.properties.root}/${id}`,
		new Headers({
			Authorization: `Bearer ${accessToken}`,
			'Content-Type': 'application/json',
		}),
		mappedData
	);

	if (!response.isOk) {
		throw new Error(response.body.message);
	}
}

/**
 * @param {string} id
 * @param {Partial<NonNullable<EditablePropertyDetails['details']>>} details
 * @param {string} accessToken
 * @returns {Promise<void>}
 * @throws {Error}
 */
export async function updatePropertyMortgageDetails(id, details, accessToken) {
	const response = await httpClient.put(
		`${apiUrls.properties.root}/${id}`,
		new Headers({
			Authorization: `Bearer ${accessToken}`,
			'Content-Type': 'application/json',
		}),
		{
			[PROPERTY_API_FIELDS.amortizationYears]: details?.amortizationYears,
			[PROPERTY_API_FIELDS.amountToLoan]: details?.amountToLoan,
			[PROPERTY_API_FIELDS.escrowInsuranceAmount]:
				details?.escrowInsuranceAmount,
			[PROPERTY_API_FIELDS.escrowInsuranceFrequency]:
				details?.escrowInsuranceFrequency,
			[PROPERTY_API_FIELDS.escrowInsuranceMonthly]:
				details?.escrowInsuranceMonthly,
			[PROPERTY_API_FIELDS.escrowTaxAmount]: details?.escrowTaxAmount,
			[PROPERTY_API_FIELDS.escrowTaxFrequency]:
				details?.escrowTaxFrequency,
			[PROPERTY_API_FIELDS.escrowTaxMonthly]: details?.escrowTaxMonthly,
			[PROPERTY_API_FIELDS.originalDateOfContract]:
				details?.originalDateOfContract,
			rate: details?.rate,
		}
	);

	if (!response.isOk) {
		throw new Error(response.body.message);
	}
}

/**
 * @param {string} id
 * @param {string} imageType
 * @param {File} file
 * @param {string} accessToken
 * @returns {Promise<void>}
 * @throws {Error}
 */
export async function updatePropertyImage(id, imageType, file, accessToken) {
	if (!file) {
		return;
	}
	const formData = new FormData();

	formData.append('files', file);

	const response = await httpClient.put(
		`${apiUrls.properties.root}/${id}/images/${imageType}`,
		new Headers({
			Authorization: `Bearer ${accessToken}`,
		}),
		formData
	);

	if (!response.isOk) {
		throw new Error(response.body.message);
	}
}
